/**
 * PricingMatrix — comparison-style pricing table.
 *
 * Used by the public marketing pages (HomePage golfer tiers, ProPage pro
 * tiers) and any in-app upgrade surface that wants the at-a-glance
 * feature × tier comparison. Audience-agnostic — the consumer supplies
 * the column (tier) definitions and the row (feature) definitions, and
 * the component handles layout, the "most popular" highlight, the
 * mobile responsive treatment, and the CTA buttons.
 *
 * Anatomy:
 *   1. Eyebrow + title + description
 *   2. Sticky header row with tier names + price + (optional) "Most
 *      popular" badge on one column
 *   3. Body rows. A row with `isSection=true` renders as a section
 *      divider ("AI features", "Bag features", etc.). Body rows can
 *      hold strings ("20/mo", "Unlimited") or booleans (✓ / —).
 *   4. Footer row with a CTA button per tier
 *
 * Mobile: < md, the columns reflow into stacked per-tier cards. The
 * desktop matrix is hidden via tailwind's md:* breakpoints. This
 * trades the at-a-glance comparison for guaranteed legibility on phone
 * widths.
 */
import type { ReactNode } from 'react';
import { Check, Minus } from 'lucide-react';

export interface PricingTier {
  /** Tier name, e.g. "Premium". Used in the header + the mobile card. */
  name: string;
  /** Short positioning line under the name. Optional. */
  tagline?: string;
  /** Display price strings. Pass null for free tiers. */
  monthlyPrice?: string | null;
  annualPrice?: string | null;
  /** Optional savings tag rendered below the annual price, e.g. "Save 16%". */
  annualSavings?: string;
  /** True for the "Most popular" / focal column. Subtle cyan tint + badge. */
  highlight?: boolean;
  /** Badge text shown when `highlight=true`. Defaults to "Most popular". */
  badge?: string;
  /** Primary CTA label, e.g. "Start free", "Go Premium". */
  ctaLabel: string;
  /** Internal anchor / external URL. Renders as an <a>. */
  ctaHref?: string;
  /** Click handler. Takes precedence over ctaHref. */
  onCtaClick?: () => void;
  /** Visual variant of the CTA. Defaults to brand-blue solid. */
  ctaVariant?: 'primary' | 'secondary';
}

export interface PricingRow {
  /** Feature label, e.g. "AI Caddie" or "Customer database". */
  label: string;
  /** Optional helper line under the label. */
  detail?: string;
  /** Cell values, one per tier (must match tiers.length). A string is
   *  rendered as-is; `true` becomes a brand-blue check; `false` / null
   *  becomes a muted dash. */
  values?: Array<string | boolean | null>;
  /** When true, renders as a section divider row spanning all columns.
   *  `values` is ignored. */
  isSection?: boolean;
}

export interface PricingMatrixProps {
  /** Short eyebrow over the title — typically a section tag like
   *  "Pricing" or "Plans". Optional. */
  eyebrow?: string;
  /** Main heading. */
  title: string;
  /** Short subheading. Optional. */
  description?: ReactNode;
  tiers: PricingTier[];
  rows: PricingRow[];
  /** Trailing note rendered below the matrix, e.g. "Prices in AUD…". */
  footnote?: ReactNode;
  /** Optional anchor id for in-page navigation (used by hamburger menu). */
  id?: string;
}

/**
 * `align` controls horizontal alignment.
 *  - "center" → desktop matrix header (table cell, want the price
 *    block centred under the tier name)
 *  - "right"  → mobile per-tier card header (price sits opposite the
 *    tier name in a flex row, so we want right-alignment)
 */
function PriceBlock({
  tier,
  align = 'center',
}: {
  tier: PricingTier;
  align?: 'center' | 'right';
}) {
  const justify = align === 'center' ? 'justify-center' : 'justify-end';
  const textAlign = align === 'center' ? 'text-center' : 'text-right';
  // Free tier — bold "Free" instead of a $0 number for stronger anchor.
  if (!tier.monthlyPrice && !tier.annualPrice) {
    return (
      <div className={textAlign}>
        <div className="text-2xl md:text-3xl font-bold text-[var(--color-on-surface)] leading-none">
          Free
        </div>
        <div className="text-xs text-[var(--color-on-subtle)] mt-1">
          forever
        </div>
      </div>
    );
  }
  return (
    <div className={`space-y-0.5 ${textAlign}`}>
      {tier.monthlyPrice && (
        <div className={`flex items-baseline gap-1.5 ${justify}`}>
          <span className="text-2xl md:text-3xl font-bold text-[var(--color-on-surface)] tabular-nums leading-none">
            {tier.monthlyPrice}
          </span>
          <span className="text-xs text-[var(--color-on-subtle)] whitespace-nowrap">/ mo</span>
        </div>
      )}
      {tier.annualPrice && (
        <div className="text-xs text-[var(--color-on-subtle)] tabular-nums">
          or {tier.annualPrice} / yr
          {tier.annualSavings && (
            <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-cyan-500/15 text-[var(--color-accent)]">
              {tier.annualSavings}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function CtaButton({ tier }: { tier: PricingTier }) {
  const primary =
    'inline-flex items-center justify-center w-full px-4 py-2.5 rounded-lg text-sm font-bold transition-colors text-white bg-[var(--color-brand)] hover:bg-[var(--color-brand-hover,var(--color-brand))]';
  const secondary =
    'inline-flex items-center justify-center w-full px-4 py-2.5 rounded-lg text-sm font-bold transition-colors text-[var(--color-on-surface)] bg-[var(--color-surface-raised)] hover:bg-[var(--color-surface-overlay,var(--color-surface-raised))] border border-[var(--color-border)]';
  const className = tier.ctaVariant === 'secondary' ? secondary : primary;
  if (tier.onCtaClick) {
    return (
      <button type="button" onClick={tier.onCtaClick} className={className}>
        {tier.ctaLabel}
      </button>
    );
  }
  if (tier.ctaHref) {
    return (
      <a href={tier.ctaHref} className={className}>
        {tier.ctaLabel}
      </a>
    );
  }
  return (
    <button type="button" disabled className={className + ' opacity-60'}>
      {tier.ctaLabel}
    </button>
  );
}

function CellValue({ value }: { value: string | boolean | null | undefined }) {
  if (value === true) {
    return <Check className="w-4 h-4 text-[var(--color-brand)] mx-auto" aria-label="Included" />;
  }
  if (value === false || value == null || value === '') {
    return <Minus className="w-4 h-4 text-[var(--color-on-muted)] mx-auto" aria-label="Not included" />;
  }
  return (
    <span className="text-sm font-medium text-[var(--color-on-surface)] tabular-nums">
      {value}
    </span>
  );
}

export function PricingMatrix({
  eyebrow,
  title,
  description,
  tiers,
  rows,
  footnote,
  id,
}: PricingMatrixProps) {
  const highlightColumn = tiers.findIndex((t) => t.highlight);

  return (
    <section
      id={id}
      // scroll-mt-24 offsets the fixed-navbar height when this section
      // is scrolled into view via a hash-anchor link (e.g. /#pricing
      // from the navbar Pricing button). Without it the heading lands
      // hidden behind the navbar.
      className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 scroll-mt-28 md:scroll-mt-32"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          {eyebrow && (
            <div
              className="inline-block uppercase tracking-widest font-bold text-xs mb-3"
              style={{ color: 'var(--color-accent)' }}
            >
              {eyebrow}
            </div>
          )}
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-on-surface)] mb-3 tracking-tight">
            {title}
          </h2>
          {description && (
            <p className="text-base text-[var(--color-on-subtle)] max-w-2xl mx-auto">
              {description}
            </p>
          )}
        </div>

        {/* ── Desktop / md+ : comparison table ───────────────────── */}
        <div className="hidden md:block overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          {/* Header row */}
          <div
            className="grid border-b border-[var(--color-border)]"
            style={{ gridTemplateColumns: `minmax(220px, 1.4fr) repeat(${tiers.length}, minmax(0, 1fr))` }}
          >
            <div className="p-5 bg-[var(--color-surface-raised)]" />
            {tiers.map((tier, i) => {
              const isHighlight = i === highlightColumn;
              return (
                <div
                  key={tier.name}
                  className="p-5 text-center border-l border-[var(--color-border)] flex flex-col items-center h-full"
                  style={{
                    backgroundColor: isHighlight
                      ? 'rgba(0, 173, 237, 0.08)'
                      : 'var(--color-surface-raised)',
                  }}
                >
                  {/* Badge — sits INSIDE the cell at the top so it can't
                       be clipped by the table wrapper's overflow-hidden.
                       Reserve the same vertical space on non-highlight
                       columns so all three tier headers line up. */}
                  <div className="h-6 flex items-center justify-center mb-2">
                    {isHighlight && (
                      <div
                        className="inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide text-white shadow"
                        style={{ backgroundColor: 'var(--color-accent)' }}
                      >
                        {tier.badge ?? 'Most popular'}
                      </div>
                    )}
                  </div>
                  <div className="text-lg font-bold text-[var(--color-on-surface)]">
                    {tier.name}
                  </div>
                  {tier.tagline && (
                    <div className="text-xs text-[var(--color-on-subtle)] mt-1 mb-3 leading-snug min-h-[2.6rem] flex items-start justify-center">
                      {tier.tagline}
                    </div>
                  )}
                  {!tier.tagline && <div className="min-h-[2.6rem] mb-3" aria-hidden="true" />}
                  <div className="mt-auto pt-1">
                    <PriceBlock tier={tier} align="center" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Body rows */}
          {rows.map((row, ri) => {
            if (row.isSection) {
              return (
                <div
                  key={`section-${ri}`}
                  className="px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest text-[var(--color-on-subtle)] bg-[var(--color-surface-raised)] border-b border-[var(--color-border)]"
                >
                  {row.label}
                </div>
              );
            }
            return (
              <div
                key={`row-${ri}`}
                className="grid border-b border-[var(--color-border)] last:border-b-0"
                style={{ gridTemplateColumns: `minmax(220px, 1.4fr) repeat(${tiers.length}, minmax(0, 1fr))` }}
              >
                <div className="p-4">
                  <div className="text-sm font-medium text-[var(--color-on-surface)]">
                    {row.label}
                  </div>
                  {row.detail && (
                    <div className="text-xs text-[var(--color-on-subtle)] mt-0.5 leading-snug">
                      {row.detail}
                    </div>
                  )}
                </div>
                {tiers.map((tier, ti) => {
                  const isHighlight = ti === highlightColumn;
                  return (
                    <div
                      key={tier.name}
                      className="p-4 text-center border-l border-[var(--color-border)] flex items-center justify-center"
                      style={{
                        backgroundColor: isHighlight
                          ? 'rgba(0, 173, 237, 0.04)'
                          : undefined,
                      }}
                    >
                      <CellValue value={row.values?.[ti]} />
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* CTA footer row */}
          <div
            className="grid border-t-2 border-[var(--color-border)] bg-[var(--color-surface-raised)]"
            style={{ gridTemplateColumns: `minmax(220px, 1.4fr) repeat(${tiers.length}, minmax(0, 1fr))` }}
          >
            <div className="p-5" />
            {tiers.map((tier, i) => {
              const isHighlight = i === highlightColumn;
              return (
                <div
                  key={tier.name}
                  className="p-5 border-l border-[var(--color-border)]"
                  style={{
                    backgroundColor: isHighlight
                      ? 'rgba(0, 173, 237, 0.08)'
                      : undefined,
                  }}
                >
                  <CtaButton tier={tier} />
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Mobile : stacked per-tier cards ─────────────────────── */}
        <div className="md:hidden space-y-4">
          {tiers.map((tier, ti) => {
            const isHighlight = ti === highlightColumn;
            return (
              <div
                key={tier.name}
                className="rounded-xl border bg-[var(--color-surface)] p-5 relative"
                style={{
                  borderColor: isHighlight ? 'var(--color-accent)' : 'var(--color-border)',
                  borderWidth: isHighlight ? 2 : 1,
                  backgroundColor: isHighlight
                    ? 'rgba(0, 173, 237, 0.04)'
                    : undefined,
                }}
              >
                {isHighlight && (
                  <div
                    className="absolute -top-2.5 left-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide text-white"
                    style={{ backgroundColor: 'var(--color-accent)' }}
                  >
                    {tier.badge ?? 'Most popular'}
                  </div>
                )}
                <div className="flex items-baseline justify-between gap-3 mb-3">
                  <div>
                    <div className="text-lg font-bold text-[var(--color-on-surface)]">
                      {tier.name}
                    </div>
                    {tier.tagline && (
                      <div className="text-xs text-[var(--color-on-subtle)] mt-0.5 leading-snug">
                        {tier.tagline}
                      </div>
                    )}
                  </div>
                  <PriceBlock tier={tier} align="right" />
                </div>

                <ul className="space-y-2 mb-4">
                  {rows
                    .filter((r) => !r.isSection)
                    .map((row, ri) => {
                      const v = row.values?.[ti];
                      const isIncluded =
                        v === true || (typeof v === 'string' && v.length > 0);
                      return (
                        <li
                          key={`m-${tier.name}-${ri}`}
                          className="flex items-start gap-2 text-sm"
                        >
                          {isIncluded ? (
                            <Check className="w-4 h-4 mt-0.5 shrink-0 text-[var(--color-brand)]" />
                          ) : (
                            <Minus className="w-4 h-4 mt-0.5 shrink-0 text-[var(--color-on-muted)]" />
                          )}
                          <span className="text-[var(--color-on-surface)]">
                            {row.label}
                            {typeof v === 'string' && v.length > 0 && (
                              <span className="text-[var(--color-on-subtle)] ml-1">
                                · {v}
                              </span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                </ul>

                <CtaButton tier={tier} />
              </div>
            );
          })}
        </div>

        {footnote && (
          <div className="mt-6 text-center text-xs text-[var(--color-on-subtle)] max-w-2xl mx-auto">
            {footnote}
          </div>
        )}
      </div>
    </section>
  );
}
